'use client'

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react'

export type ToastTone = 'success' | 'critical' | 'info'

export interface ToastState {
  content: string
  tone?: ToastTone
}

interface ToastContextValue {
  toast: ToastState | null
  showToast: (content: string, tone?: ToastTone) => void
  dismissToast: () => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

const TOAST_DURATION_MS = 4000

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<ToastState | null>(null)

  const showToast = useCallback((content: string, tone: ToastTone = 'success') => {
    setToast({ content, tone })
  }, [])

  useEffect(() => {
    if (!toast) return
    const t = setTimeout(() => setToast(null), TOAST_DURATION_MS)
    return () => clearTimeout(t)
  }, [toast])

  const dismissToast = useCallback(() => {
    setToast(null)
  }, [])

  const value: ToastContextValue = {
    toast,
    showToast,
    dismissToast,
  }

  return (
    <ToastContext.Provider value={value}>
      {children}
    </ToastContext.Provider>
  )
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext)
  if (!ctx) {
    throw new Error('useToast must be used within ToastProvider')
  }
  return ctx
}
